<template>
  <div class="insights-treeview-container">
    <div class="insights-treeview-header">
      <span class="insights-treeview-title">Insights por cidade</span>
      <Button
        v-if="Object.keys(expandedKeys).length < filteredTreeNodes.length"
        icon="pi pi-plus"
        class="p-button-rounded p-button-text"
        @click="expandAll"
        title="Expandir todos"
      />
      <Button
        v-else
        icon="pi pi-minus"
        class="p-button-rounded p-button-text"
        @click="collapseAll"
        title="Fechar todos"
      />
    </div>

    <!-- Busca -->
    <div class="insights-treeview-search">
      <span class="p-input-icon-left w-full">
        <i class="pi pi-search" />
        <InputText
          v-model="searchQuery"
          placeholder="Buscar insights..."
          class="w-full"
        />
      </span>
    </div>

    <!-- TreeView -->
    <div class="insights-treeview-content">
      <Tree
        :value="filteredTreeNodes"
        :expandedKeys="expandedKeys"
        selectionMode="single"
        @node-select="handleNodeSelect"
        @node-expand="handleNodeExpand"
        @node-collapse="handleNodeCollapse"
        class="insights-tree"
        :pt="{
          togglerIcon: { class: 'hidden-element' },
          nodeIcon: { class: 'hidden-element' },
          content: { class: 'custom-treenode-content' }
        }"
      >
        <template #default="{ node }">
          <div class="insights-tree-node">
            <!-- Cidade -->
            <div v-if="node.type === 'city'" class="city-node">
              <i class="pi pi-map-marker city-icon"></i>
              <span class="node-label">{{ node.label }}</span>
              <Badge :value="node.totalInsights" class="insights-count-badge" severity="info" />
            </div>
            
            <!-- Insight -->
            <div v-else-if="node.type === 'insight'" class="insight-node">
              <div class="insight-content">
                <div class="insight-header">
                  <div class="insight-title">{{ node.label }}</div>
                  <div class="insight-info">
                    <img src="/lamp-icon.png" alt="Ícone Insights" class="info-icon" />
                    <span class="info-text">
                      {{ node.insightCount }}
                      {{ node.insightCount > 1 ? 'insights' : 'insight' }}
                    </span>
                  </div>
                </div>
                <button
                  class="open-button"
                  @click.stop="openInsight(node.data)"
                >
                  <img src="/lamp-icon.png" alt="Ícone" class="button-icon" />
                  <span>Abrir</span>
                </button>
              </div>
            </div>
          </div>
        </template>
      </Tree>
      
      <!-- Mensagem quando não há resultados -->
      <div v-if="filteredTreeNodes.length === 0" class="no-results">
        <i class="pi pi-search no-results-icon"></i>
        <p>Nenhum insight encontrado para sua busca.</p>
      </div>
    </div>
  </div>
</template>

<script>
import Tree from 'primevue/tree';
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';

export default {
  name: 'InsightsTreeView',
  components: {
    Tree,
    Badge,
    Button,
    InputText
  },
  props: {
    module: {
      type: String,
      required: true
    },
    insights: {
      type: Array,
      default: () => []
    },
    states: {
      type: Array,
      default: () => []
    },
    getPromptTypeLabel: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      expandedKeys: {},
      searchQuery: '',
      treeNodes: []
    }
  },
  computed: {
    filteredTreeNodes() {
      if (!this.searchQuery) {
        return this.treeNodes;
      }
      
      const query = this.searchQuery.toLowerCase();
      
      // Filtrar cidades e insights que correspondem à busca
      return this.treeNodes.filter(cityNode => {
        // Verificar se o nome da cidade corresponde
        if (cityNode.label.toLowerCase().includes(query)) {
          return true;
        }
        
        // Criar cópias filtradas dos insights dentro da cidade
        const filteredInsights = cityNode.children.filter(insightNode => 
          insightNode.label.toLowerCase().includes(query)
        );
        
        // Se há insights correspondentes, atualizar as crianças da cidade
        if (filteredInsights.length > 0) {
          cityNode.children = filteredInsights;
          return true;
        }
        
        return false;
      });
    }
  },
  watch: {
    insights: {
      immediate: true,
      handler(newInsights) {
        this.processInsightsToTree(newInsights);
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.removeTreeTogglers();
      // Adicionar um observador para lidar com elementos adicionados dinamicamente
      this.setupMutationObserver();
    });
  },
  methods: {
    // Remove os botões toggler e ícones da árvore
    removeTreeTogglers() {
      const togglers = document.querySelectorAll('.p-tree-toggler');
      const icons = document.querySelectorAll('.p-treenode-icon');
      
      togglers.forEach(toggler => {
        toggler.style.display = 'none';
        toggler.style.width = '0';
        toggler.style.height = '0';
        toggler.style.padding = '0';
        toggler.style.margin = '0';
        toggler.style.visibility = 'hidden';
        toggler.style.position = 'absolute';
      });
      
      icons.forEach(icon => {
        icon.style.display = 'none';
        icon.style.width = '0';
        icon.style.height = '0';
        icon.style.padding = '0';
        icon.style.margin = '0';
        icon.style.visibility = 'hidden';
        icon.style.position = 'absolute';
      });
      
      // Ajustar o padding dos nós
      const contents = document.querySelectorAll('.p-treenode-content');
      contents.forEach(content => {
        content.style.paddingLeft = '0.25rem';
      });
    },
    
    // Configura um observador de mutação para lidar com elementos adicionados dinamicamente
    setupMutationObserver() {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length) {
            this.removeTreeTogglers();
          }
        });
      });
      
      const treeContainer = document.querySelector('.insights-tree');
      if (treeContainer) {
        observer.observe(treeContainer, {
          childList: true,
          subtree: true
        });
      }
    },
    processInsightsToTree(insightsArray) {
      // Estruturar os dados dos insights em árvore agrupando por cidade
      const cityMap = new Map();
      
      // Primeiro, agrupar insights por cidade
      insightsArray.forEach(insight => {
        let cityName = insight.city || 'Sem localidade';
        
        // Se o nome da cidade incluir estado (formato "Cidade - Estado")
        if (cityName.includes(' - ')) {
          cityName = cityName.split(' - ')[0].trim();
        }
        
        // Criar ou obter o objeto de cidade
        if (!cityMap.has(cityName)) {
          cityMap.set(cityName, {
            key: `city-${cityName}`,
            type: 'city',
            label: cityName,
            selectable: false,
            insights: [],
            totalInsights: 0
          });
        }
        
        const cityObj = cityMap.get(cityName);
        cityObj.totalInsights++;
        
        // Adicionar insight à cidade
        const insightKey = `insight-${cityName}-${insight.id || Math.random().toString(36)}`;
        cityObj.insights.push({
          key: insightKey,
          type: 'insight',
          label: insight.title || this.getPromptTypeLabel(insight.type),
          selectable: true,
          insightCount: this.getInsightCount(insight),
          data: insight
        });
      });
      
      // Transformar o mapa em array para o TreeView
      const treeNodes = [];
      
      cityMap.forEach(cityObj => {
        const cityNode = {
          key: cityObj.key,
          type: cityObj.type,
          label: cityObj.label,
          selectable: false,
          totalInsights: cityObj.totalInsights,
          children: cityObj.insights
        };
        
        treeNodes.push(cityNode);
      });
      
      // Ordenar cidades por nome
      treeNodes.sort((a, b) => a.label.localeCompare(b.label));
      
      this.treeNodes = treeNodes;
      
      // Expandir a primeira cidade por padrão se houver
      if (treeNodes.length > 0) {
        this.expandedKeys[treeNodes[0].key] = true;
      }
    },
    
    handleNodeSelect(node) {
      // Desabilitando a abertura automática quando o nó é selecionado
      // Agora o insight só será aberto quando o botão "Abrir" for clicado
    },
    
    handleNodeExpand(node) {
      this.expandedKeys[node.key] = true;
    },
    
    handleNodeCollapse(node) {
      this.expandedKeys[node.key] = false;
    },
    
    expandAll() {
      // Expandir todas as cidades
      const keys = {};
      this.treeNodes.forEach(node => {
        keys[node.key] = true;
      });
      this.expandedKeys = keys;
    },
    
    collapseAll() {
      this.expandedKeys = {};
    },
    
    openInsight(insight) {
      this.$emit('open-insights', insight);
    },
    
    getInsightCount(insight) {
      if (insight.items) {
        return insight.items.length;
      }
      if (insight.length) {
        return insight.length;
      }
      return 1;
    }
  }
}
</script>

<style>
/* Regras diretas para remover completamente o botão toggler e o ícone */
.p-tree .p-tree-container .p-treenode .p-tree-toggler {
  display: none !important;
  width: 0 !important;
  height: 0 !important;
  visibility: hidden !important;
  position: absolute !important;
}

.p-tree .p-tree-container .p-treenode .p-treenode-icon {
  display: none !important;
  width: 0 !important;
  height: 0 !important;
  visibility: hidden !important;
  position: absolute !important;
}

/* Ajustar o conteúdo do nó para compensar os elementos removidos */
.p-tree .p-tree-container .p-treenode .p-treenode-content {
  padding-left: 0.25rem !important;
}

/* Garantir que o label ocupe todo o espaço disponível */
.p-tree .p-tree-container .p-treenode .p-treenode-label {
  margin-left: 0 !important;
  padding-left: 0 !important;
  width: 100% !important;
}

/* Remover efeito de hover nos nós da árvore */
.p-tree .p-tree-container .p-treenode .p-treenode-content:hover,
.p-tree .p-tree-container .p-treenode .p-treenode-content:focus,
.p-tree .p-tree-container .p-treenode .p-treenode-content:active,
.p-tree .p-tree-container .p-treenode .p-treenode-content.p-highlight,
.p-tree .p-tree-container .p-treenode .p-treenode-content.p-highlight:hover,
.p-tree .p-tree-container .p-treenode .p-treenode-content.p-highlight:focus,
.p-tree .p-tree-container .p-treenode .p-treenode-content.p-highlight:active {
  background-color: transparent !important;
  color: inherit !important;
  box-shadow: none !important;
  border-color: transparent !important;
}

/* Remover outline ao focar */
.p-tree .p-tree-container .p-treenode .p-treenode-content:focus {
  outline: none !important;
}
.insights-treeview-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  padding: 0 20px;
  width: 100%;
}

.insights-treeview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #eaeaea;
}

.insights-treeview-title {
  font-size: 18px;
  font-weight: 600;
  color: #1c3d5a;
}

.insights-treeview-search {
  margin-bottom: 20px;
}

.insights-treeview-content {
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
  padding-bottom: 24px;
  width: 100%;
}

.insights-tree {
  border: none;
  background: transparent;
  width: 100%;
}

.insights-tree-node {
  display: flex;
  align-items: center;
  padding: 6px 0;
  width: 100%;
}

.city-node {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 14px 20px;
  border-radius: 10px;
  background-color: #f0f7ff;
  margin-bottom: 12px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.city-node:hover {
  background-color: #e0f0ff;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
}

.city-icon {
  color: #1c3d5a;
  margin-right: 14px;
  font-size: 1.3rem;
  min-width: 24px;
}

.node-label {
  flex: 1;
  font-weight: 600;
  font-size: 16px;
  color: #1c3d5a;
  padding-right: 10px;
  white-space: normal;
  word-break: break-word;
}

.insights-count-badge {
  font-size: 0.9rem;
  text-align: center;
}

.insight-node {
  padding: 20px;
  background-color: #ffffff;
  border-radius: 10px;
  width: calc(100% - 20px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  border-left: 4px solid #1c3d5a;
}

.insight-node:hover {
  background-color: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.insight-content {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.insight-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}

.insight-title {
  font-weight: 600;
  margin-bottom: 10px;
  color: #333;
  font-size: 16px;
  line-height: 1.5;
  word-break: break-word;
}

.insight-info {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.info-icon {
  width: 18px;
  height: 18px;
  margin-right: 8px;
}

.info-text {
  font-size: 13px;
  color: #6c757d;
}

.insight-open-button {
  align-self: flex-end;
  background-color: #1c3d5a;
  border-color: #1c3d5a;
  transition: all 0.3s ease;
  min-width: 100px;
  margin-top: 8px;
}

.insight-open-button:hover {
  background-color: #2c4a66;
  border-color: #2c4a66;
  transform: translateY(-2px);
  box-shadow: 0 3px 6px rgba(28, 61, 90, 0.2);
}

.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: #6c757d;
}

.no-results-icon {
  font-size: 3rem;
  margin-bottom: 16px;
  opacity: 0.3;
}

/* Estilo específico para a PrimeVue Tree */
:deep(.p-tree) {
  border: none;
  padding: 0;
  width: 100%;
}

:deep(.p-treenode-content) {
  border-radius: 8px;
  transition: all 0.2s ease;
  padding: 6px 0;
  width: 100%;
}

:deep(.p-treenode-content:hover) {
  background-color: transparent !important;
}

:deep(.p-treenode-leaf > .p-treenode-content) {
  padding: 0;
  width: 100%;
}

:deep(.p-tree-toggler) {
  margin-right: 0.5rem;
  width: 2rem;
  height: 2rem;
}

:deep(.p-tree-toggler-icon) {
  color: #1c3d5a;
  font-size: 1rem;
}

:deep(.p-treenode-children) {
  padding-left: 2rem;
  margin-top: 8px;
  width: 100%;
}

.open-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  background-color: #ffffff;
  color: #1c3d5a;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
  align-self: flex-end;
  min-width: 100px;
  margin-top: 8px;
}

.open-button:hover {
  background-color: #f0f5ff;
  color: #0047ab;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
  transform: translateY(-1px);
}

.button-icon {
  width: 16px;
  height: 16px;
  margin-right: 8px;
}
</style> 